import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { mapProps } from '../../../../../../../util/hocs';
import TeamDetailsSelection from './TeamDetailsSelection';
import Button from '../../../../../../../components/global/Button';
import { AlbumFolderTags } from '../../../../../../../graphql/queries/album-folder-tags';
import Placeholder from './Placeholder';
import {
  albumFolderRequiresTagsToOnboard,
  canConfigureAdditionalTeam,
  getEligibleTagsByIdentityGender,
  getInvalidJerseyNumbersFromSelectedTags,
  hasAvailableUniformTagInfoForOnboarding,
  isUniformAlbumFolderTagConfigurable,
  mapIdentityAlbumFolderTagsToSelectedTags,
  mapSelectedTagsToIdentityAlbumFolderTagsInput,
} from '../../helpers';
import { getTagsByType } from '../../../../../../../util/album-folder-tags';
import {
  identityAlbumFolderTagsCreate as identityAlbumFolderTagsCreateMutation,
  identityAlbumFolderTagsDelete as identityAlbumFolderTagsDeleteMutation,
} from '../../../../../../../graphql/mutations/album-folder-tags';
import Alert from '../../../../../../../components/global/Alert';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import BoxedItem from '../../../../../../../components/global/BoxedItem';
import getIdentityAlbumFolderTags, {
  getIdentityAlbumFolderTagsQuery,
} from '../../graphql/queries/getIdentityAlbumFolderTags';
import { ALBUM_FOLDER_TAG_TYPE } from '../../../../../../../constants/album-folder-tags';
import TagSelectionScroller from './TagSelectionScroller';
import { IDENTITY_GENDER } from '../../../../../../../constants/identity';
import IdentityGenderSelectionButtons from '../../../../../../consumer/components/IdentityGenderSelectionButtons';
import Label from '../../../../../../../components/global/Label';
import { identityUpdate as identityUpdateMutation } from '../../../../../../consumer/graphql/mutations/identity';
import { getAsset } from '../../../../../../../util/assets';
import { getIdentityName } from '../../../../../../../util/identity';
import Angle from '../../../../../../../components/global/Angle';
import useNumberedJerseyRecognition from './hooks/useNumberedJerseyRecognition';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      uniform_enabled: PropTypes.bool,
      uniform_numbered: PropTypes.bool,
      levels: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            teams: PropTypes.shape({
              records: PropTypes.arrayOf(
                PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string,
                  coach_name: PropTypes.string,
                }),
              ),
            }),
          }),
        ),
      }),
      gender: PropTypes.oneOf(Object.values(IDENTITY_GENDER)),
    }),
  ),
  identityAlbumFolderTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      album_folder_tag: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      uniform_album_folder_tag_level: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      uniform_album_folder_tag_level_team: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      jersey_number: PropTypes.string,
    }),
  ),
  identityAlbumFolderTagsCreate: PropTypes.func.isRequired,
  identityAlbumFolderTagsDelete: PropTypes.func.isRequired,
  identityUpdate: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  onSkip: PropTypes.func,
  onSkipText: PropTypes.string,
  className: PropTypes.string,
  loadingTags: PropTypes.bool,
  shootType: PropTypes.string,
  rootAlbumFolderId: PropTypes.string,
};

const STEPS = {
  TAG_SELECT: 'TAG_SELECT',
  TAG_CONFIGURE: 'TAG_CONFIGURE',
};

const IdentityTagSelection = ({
  albumFolder,
  tags,
  identity,
  identityAlbumFolderTags,
  identityAlbumFolderTagsCreate,
  identityAlbumFolderTagsDelete,
  identityUpdate,
  onComplete,
  onError,
  onSkip,
  onSkipText = "I'm not sure - Skip",
  className,
  loadingTags,
  shootType,
  rootAlbumFolderId,
}) => {
  const [step, setStep] = useState(STEPS.TAG_SELECT);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const numberedJerseyRecognitionEnabled = useNumberedJerseyRecognition({
    shootType,
    rootAlbumFolderId,
  });

  if (loadingTags || !identityAlbumFolderTags) {
    return <Placeholder />;
  }

  const classes = classNames({
    'identity-tag-selection-form': true,
    [className]: !!className,
  });

  // Avoid deleting grade (or any other really) types of tags.
  const identityAlbumFolderUniformTags = identityAlbumFolderTags.filter(
    identityAlbumFolderTag =>
      identityAlbumFolderTag.album_folder_tag.type ===
      ALBUM_FOLDER_TAG_TYPE.UNIFORM,
  );

  const initialEligibleTags = getEligibleTagsByIdentityGender(
    tags,
    identity?.gender,
    !albumFolderRequiresTagsToOnboard(albumFolder),
  );

  // We do this with the overall initial tags rather than doing it in the form
  // since the form alters the eligible tags based off gender. This could mean
  // the user chooses a gender that only has 1 tag, but since they have not done a
  // definitive selection, we must avoid withholding their ability to switch between genders
  const isSingleTagOnboarding = initialEligibleTags.length === 1;
  const albumFolderTag = initialEligibleTags?.[0];
  const showConfigurationDetails =
    step === STEPS.TAG_CONFIGURE || isSingleTagOnboarding;

  const onFormSubmit = async values => {
    const eligibleTags = getEligibleTagsByIdentityGender(
      tags,
      identity?.gender || values.gender,
      !albumFolderRequiresTagsToOnboard(albumFolder),
    );

    if (
      step === STEPS.TAG_SELECT &&
      !isSingleTagOnboarding &&
      eligibleTags.length > 0
    ) {
      setStep(STEPS.TAG_CONFIGURE);
      return;
    }
    const input = mapSelectedTagsToIdentityAlbumFolderTagsInput(
      identity,
      values.selectedTags,
      values.configuringTagIds,
    );

    setSubmitting(true);
    setSubmitError('');

    try {
      const identityAlbumFolderTagIds = identityAlbumFolderUniformTags.map(
        ({ id }) => id,
      );

      // If the gender was missing update it
      if (!identity.gender && values.gender) {
        await identityUpdate({
          id: identity.id,
          name: identity.name,
          gender: values.gender,
        });
      }

      if (identityAlbumFolderTagIds.length > 0 && input) {
        // For brevity, simply delete all existing tags and re-create them -- it's
        // harmless and saves a lot of logic determining what changed
        // and what didn't.
        await identityAlbumFolderTagsDelete(identityAlbumFolderTagIds);
      }

      if (input) {
        await identityAlbumFolderTagsCreate(input);
      }
      setSubmitting(false);
      onComplete?.(identity);
    } catch (error) {
      setSubmitError(getErrorString(error));
      setSubmitting(false);
      onError?.(error);
    }
  };

  return (
    <Formik
      initialValues={{
        configuringTagIds: isSingleTagOnboarding ? [albumFolderTag.id] : [],
        selectedTags: mapIdentityAlbumFolderTagsToSelectedTags(
          tags,
          identityAlbumFolderTags,
        ),
        // Since the tags have been pre-filtered when accounting for isSingleTagOnboarding
        // we're fine with selecting albumFolderTag.gender as it either, matches the identity's
        // or adds one of the identity doesn't have it.
        gender: isSingleTagOnboarding
          ? (albumFolderTag.gender ?? identity.gender ?? undefined)
          : (identity.gender ?? undefined),
      }}
      validate={values => {
        const errors = {};

        if (!identity.gender && !values.gender && !!albumFolderTag?.gender) {
          errors.gender = 'Please help Waldo by selecting a gender.';
        }

        const invalidJerseyNumbers = getInvalidJerseyNumbersFromSelectedTags(
          values.selectedTags,
        );

        if (invalidJerseyNumbers.length > 0) {
          errors.jerseyNumbers =
            'One or more jersey numbers are invalid. Please use a number between 00 and 99';
        }

        return errors;
      }}
      onSubmit={onFormSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      render={({ values, errors, handleSubmit, handleChange }) => {
        const input = mapSelectedTagsToIdentityAlbumFolderTagsInput(
          identity,
          values.selectedTags,
          values.configuringTagIds,
        );
        const eligibleAlbumFolderTags = getEligibleTagsByIdentityGender(
          tags,
          identity?.gender || values.gender,
          !albumFolderRequiresTagsToOnboard(albumFolder),
        );
        const formikErrors = Object.values(errors);
        const showGenderSelection = (() => {
          if (isSingleTagOnboarding) {
            return !!albumFolderTag?.gender && !identity?.gender;
          }

          return !identity?.gender;
        })();

        return (
          <form onSubmit={handleSubmit} className={classes}>
            {
              // Only ask for gender if the Identity does not have one.
              showGenderSelection && (
                <Fragment>
                  <Label
                    id="identity-gender"
                    labelType="expanded"
                    label="Gender"
                  />
                  <IdentityGenderSelectionButtons
                    value={values.gender}
                    onChange={value => {
                      // If the user selects a different gender reset the
                      // tag selection.
                      if (value !== values.gender && !isSingleTagOnboarding) {
                        handleChange({
                          target: {
                            name: 'configuringTagIds',
                            value: [],
                          },
                        });
                      }

                      handleChange({
                        target: {
                          name: 'gender',
                          value,
                        },
                      });
                    }}
                    className="mb-3"
                  />
                </Fragment>
              )
            }

            {step === STEPS.TAG_SELECT && (
              <div className="identity-tag-selection-tag-select">
                {eligibleAlbumFolderTags.length >= 1 &&
                  !isSingleTagOnboarding && (
                    <Fragment>
                      <h4 className="mb-1">Select Sport</h4>
                      <TagSelectionScroller
                        albumFolderTags={eligibleAlbumFolderTags}
                        identityAlbumFolderTags={identityAlbumFolderTags}
                        configuringTagIds={values.configuringTagIds}
                        onTagSelected={tag => {
                          if (values.configuringTagIds.includes(tag.id)) {
                            handleChange({
                              target: {
                                name: 'configuringTagIds',
                                value: values.configuringTagIds.filter(
                                  id => id !== tag.id,
                                ),
                              },
                            });
                          } else {
                            handleChange({
                              target: {
                                name: 'configuringTagIds',
                                value: [...values.configuringTagIds, tag.id],
                              },
                            });
                          }
                        }}
                        onRemoveTag={async tagId => {
                          await identityAlbumFolderTagsDelete([tagId]);
                        }}
                      />
                    </Fragment>
                  )}

                {eligibleAlbumFolderTags.length === 0 && (
                  <Fragment>
                    <Alert type="info">
                      <img
                        src={getAsset('/waldo-icon.svg')}
                        alt="Waldo"
                        width="32"
                        className="mb-1 d-block mx-auto"
                      />
                      <p className="text-base text-center">
                        No sports or activities were found in this Gallery for{' '}
                        {getIdentityName(identity)}.
                        {albumFolderRequiresTagsToOnboard(albumFolder)
                          ? ''
                          : " Feel free to continue below and I'll reach out when I need more info."}
                      </p>
                    </Alert>
                  </Fragment>
                )}
              </div>
            )}

            {showConfigurationDetails && (
              <Fragment>
                {!isSingleTagOnboarding && (
                  <div className="w-100 mb-1">
                    <Button
                      btnStyle="link"
                      onClick={() => setStep(STEPS.TAG_SELECT)}
                    >
                      <Angle direction="left" /> Back
                    </Button>
                  </div>
                )}
                <div className="team-details-selection-list">
                  {values.configuringTagIds.map(tagId => {
                    const tag = eligibleAlbumFolderTags.find(
                      ({ id }) => id === tagId,
                    );

                    if (!tag) {
                      return null;
                    }

                    return (
                      <div className="mb-2">
                        <h4>{tag.name}</h4>
                        {values.selectedTags[tagId].map(
                          (selectedTag, index) => {
                            return (
                              <BoxedItem key={index} className="mb-1">
                                <TeamDetailsSelection
                                  key={index}
                                  index={index}
                                  tag={tags.find(({ id }) => id === tagId)}
                                  value={selectedTag}
                                  handleChange={handleChange}
                                  numberedJerseyRecognitionEnabled={
                                    numberedJerseyRecognitionEnabled
                                  }
                                />
                              </BoxedItem>
                            );
                          },
                        )}

                        {canConfigureAdditionalTeam(
                          values.selectedTags[tagId],
                        ) && (
                          <Button
                            btnBlock
                            btnStyle="link"
                            className="mb-3 text-center"
                            type="button"
                            onClick={() => {
                              handleChange({
                                target: {
                                  name: 'selectedTags',
                                  value: {
                                    ...values.selectedTags,
                                    [tagId]: [
                                      ...values.selectedTags[tagId],
                                      {
                                        tagId,
                                        levelId: '',
                                        teamId: '',
                                        jerseyNumber: '',
                                      },
                                    ],
                                  },
                                },
                              });
                            }}
                          >
                            Add new team&hellip;
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Fragment>
            )}

            <div className="identity-tag-selection-actions">
              {(submitError || formikErrors.length > 0) && (
                <Alert type="danger">
                  {submitError ||
                    formikErrors.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                </Alert>
              )}

              {(values.configuringTagIds.length > 0 ||
                eligibleAlbumFolderTags.length === 0) && (
                <Button
                  btnBlock
                  type="submit"
                  disabled={
                    submitting ||
                    (!input &&
                      showConfigurationDetails &&
                      eligibleAlbumFolderTags.length > 0) ||
                    (albumFolderRequiresTagsToOnboard(albumFolder) &&
                      !values.configuringTagIds.length) ||
                    (showConfigurationDetails &&
                      !hasAvailableUniformTagInfoForOnboarding({
                        albumFolder,
                        albumFolderTags: tags.filter(({ id }) =>
                          values.configuringTagIds.includes(id),
                        ),
                        input,
                        numberedJerseyRecognitionEnabled,
                      }))
                  }
                >
                  {(() => {
                    if (submitting) {
                      return 'Loading...';
                    }

                    if (showConfigurationDetails) {
                      return 'Confirm Details';
                    }

                    if (eligibleAlbumFolderTags.length === 0) {
                      return 'Continue';
                    }

                    return 'Configure Tags';
                  })()}
                </Button>
              )}

              {onSkip && (
                <Button
                  btnStyle="link"
                  className="text-center"
                  btnBlock
                  onClick={() => onSkip(identity)}
                >
                  {onSkipText}
                </Button>
              )}
            </div>
          </form>
        );
      }}
    />
  );
};

IdentityTagSelection.propTypes = propTypes;

const withAlbumFolderTags = graphql(AlbumFolderTags, {
  options: ({ match, albumFolder }) => ({
    variables: {
      id: albumFolder?.id ?? match?.params?.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder, loading: loadingTags } = {} }) => ({
    tags: getTagsByType(
      ALBUM_FOLDER_TAG_TYPE.UNIFORM,
      albumFolder?.tags?.records,
    ).filter(isUniformAlbumFolderTagConfigurable),
    loadingTags,
    shootType: albumFolder?.shoot_type,
    rootAlbumFolderId: albumFolder?.root?.id ?? albumFolder?.id,
  }),
});

const withIdentityAlbumFolderTagsCreate = graphql(
  identityAlbumFolderTagsCreateMutation,
  {
    props: ({ mutate, ownProps: { identityId, albumFolderId } }) => ({
      identityAlbumFolderTagsCreate: input =>
        mutate({
          variables: {
            input,
          },
          refetchQueries: [
            {
              query: getIdentityAlbumFolderTagsQuery,
              variables: {
                identityId,
                albumFolderId,
              },
            },
          ],
        }),
    }),
  },
);

const withIdentityAlbumFolderTagsDelete = graphql(
  identityAlbumFolderTagsDeleteMutation,
  {
    props: ({ mutate, ownProps: { identityId, albumFolderId } }) => ({
      identityAlbumFolderTagsDelete: ids =>
        mutate({
          variables: {
            input: {
              ids,
            },
          },
          refetchQueries: [
            {
              query: getIdentityAlbumFolderTagsQuery,
              variables: {
                identityId,
                albumFolderId,
              },
            },
          ],
        }),
    }),
  },
);

const withIdentityUpdate = graphql(identityUpdateMutation, {
  props: ({ mutate }) => ({
    identityUpdate: ({ id, name, gender }) =>
      mutate({
        variables: {
          input: {
            id,
            name,
            gender,
          },
        },
      }),
  }),
});

export default compose(
  withRouter,
  withAlbumFolderTags,
  mapProps(({ ...props }) => ({
    ...props,
    identityId: props.identity?.id,
    albumFolderId: props.albumFolder?.id,
  })),
  getIdentityAlbumFolderTags,
  mapProps(({ data, ...props }) => ({
    ...props,
    identityAlbumFolderTags: data?.identity?.album_folder_tags?.records,
  })),
  withIdentityAlbumFolderTagsCreate,
  withIdentityAlbumFolderTagsDelete,
  withIdentityUpdate,
)(IdentityTagSelection);
